import { useEffect, useState, useCallback } from "react";
import API_BASE_URL from "../config";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { MapPin, Plus, Grid, Map as MapIcon, Star, Eye, Heart } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { toast } from "sonner";
import ImageWithFallback from "../components/ui/ImageWithFallback";

const API = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;

const CATEGORIES = [
  "All", "temple", "trek", "lake", "waterfall", "hill_station",
  "wildlife", "camping", "village", "historical", "other",
];

const markerIcon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

const PLACEHOLDER_IMGS = [
  "https://images.unsplash.com/photo-1542721847-51ed6bd083ef?w=400",
  "https://images.unsplash.com/photo-1717748973244-6371f2a06849?w=400",
  "https://images.unsplash.com/photo-1644598881885-3a14deee6fe0?w=400",
];

export default function Places() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [places, setPlaces] = useState([]);
  const [mapPlaces, setMapPlaces] = useState([]);
  const [view, setView] = useState("grid");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(true);

  const fetchPlaces = useCallback(async () => {
    setLoading(true);
    try {
      const params = { exclude_gibberish: true };
      if (category !== "All") params.category = category;
      if (search) params.search = search;

      const { data } = await axios.get(`${API}/places`, {
        params,
        withCredentials: true,
      });
      setPlaces(data.places || []);
    } catch (err) {
      console.error("Failed to load places:", err);
      toast.error("Failed to load places");
    } finally {
      setLoading(false);
    }
  }, [category, search]);

  const fetchMapPlaces = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/places/map`, {
        withCredentials: true,
      });
      setMapPlaces(data.places || data || []);
    } catch (err) { console.error("Failed to fetch map places", err); }
  }, []);

  useEffect(() => {
    fetchPlaces();
    fetchMapPlaces();
  }, [fetchPlaces, fetchMapPlaces]);

  const getPlaceImage = (place, idx) => {
    if (place.images?.length > 0) {
      let img = place.images[0];
      if (typeof img === "string") {
        if (img.includes("https://res.cloudinary.com")) {
          img = img.substring(img.indexOf("https://res.cloudinary.com"));
        }
        if (/^https?:\/\//i.test(img)) return img;
      }
      const base = process.env.REACT_APP_BACKEND_URL || API_BASE_URL;
      return `${base}${img?.startsWith("/") ? "" : "/"}${img}`;
    }
    return PLACEHOLDER_IMGS[idx % PLACEHOLDER_IMGS.length];
  };

  const handleLike = async (e, placeId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.error("Please login to like places");
      return;
    }
    try {
      const place = places.find(p => (p.id || p._id) === placeId);
      const userId = user.id || user._id;
      const isLiked = place.liked_by?.includes(userId);
      
      const endpoint = isLiked ? `/places/${placeId}/unlike` : `/places/${placeId}/like`;
      await axios.post(`${API}${endpoint}`, {}, { withCredentials: true });
      
      setPlaces(places.map(p => {
        if ((p.id || p._id) === placeId) {
          const newLikedBy = isLiked 
            ? (p.liked_by || []).filter(id => id !== userId)
            : [...(p.liked_by || []), userId];
          return {
            ...p,
            liked_by: newLikedBy,
            likes: Math.max(0, (p.likes || 0) + (isLiked ? -1 : 1))
          };
        }
        return p;
      }));
    } catch (err) {
      toast.error("Failed to like place");
    }
  };

  const formatCategory = (cat) => {
    if (!cat) return "";
    return String(cat).replaceAll("_", " ");
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f2f0eb', paddingBottom: 80 }}>
      {/* Header */}
      <div style={{ background: '#0f4a3c', padding: '48px 20px 36px' }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: 'white', marginBottom: 8 }}>
            Discover Places
          </h1>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.8)', lineHeight: 1.6 }}>
            Find temples, treks, hill stations, and more across Uttarakhand
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 700, margin: '0 auto', padding: '20px 16px' }}>
        {/* Controls */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 16 }}>
          {/* Search */}
          <div className="flex flex-col md:flex-row gap-3 md:items-center">
            <input
              type="text"
              placeholder="Search places (e.g., Kedarnath, Auli)..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                flex: 1, background: 'white', border: '1px solid rgba(0,0,0,0.1)',
                borderRadius: 999, padding: '10px 18px', fontSize: 14,
                color: '#1a2e1a', outline: 'none', transition: 'border-color 0.2s',
                width: '100%'
              }}
              onFocus={e => { e.target.style.borderColor = '#1D9E75'; }}
              onBlur={e => { e.target.style.borderColor = 'rgba(0,0,0,0.1)'; }}
            />

            <div className="flex justify-between items-center gap-3">
              {/* View toggle */}
              <div style={{ background: 'rgba(0,0,0,0.05)', borderRadius: 12, padding: 4, display: 'flex', gap: 2, flexShrink: 0 }}>
                <button
                  type="button"
                  onClick={() => setView("grid")}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    borderRadius: 8, padding: '7px 12px', fontSize: 13,
                    fontWeight: 600, border: 'none', cursor: 'pointer', transition: 'all 0.2s',
                    background: view === "grid" ? '#1a3a1a' : 'transparent',
                    color: view === "grid" ? 'white' : '#555',
                  }}
                >
                  <Grid size={15} /> Grid
                </button>
                <button
                  type="button"
                  onClick={() => setView("map")}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    borderRadius: 8, padding: '7px 12px', fontSize: 13,
                    fontWeight: 600, border: 'none', cursor: 'pointer', transition: 'all 0.2s',
                    background: view === "map" ? '#1a3a1a' : 'transparent',
                    color: view === "map" ? 'white' : '#555',
                  }}
                >
                  <MapIcon size={15} /> Map
                </button>
              </div>

              {user && (
                <button
                  onClick={() => navigate("/places/add")}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: '#1D9E75', color: 'white', border: 'none',
                    borderRadius: 999, padding: '10px 16px', fontSize: 13,
                    fontWeight: 600, cursor: 'pointer', flexShrink: 0,
                    whiteSpace: 'nowrap'
                  }}
                >
                  <Plus size={15} /> Add
                </button>
              )}
            </div>
          </div>

          {/* Categories */}
          <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }} className="scrollbar-hide">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                style={{
                  padding: '8px 16px', borderRadius: 999, fontSize: 13, fontWeight: 500,
                  whiteSpace: 'nowrap', border: 'none', cursor: 'pointer', transition: 'all 0.2s',
                  background: category === cat ? '#1a3a1a' : 'white',
                  color: category === cat ? 'white' : '#555',
                  border: category === cat ? '1px solid #1a3a1a' : '1px solid rgba(0,0,0,0.12)',
                }}
              >
                {cat === "All" ? "All Places" : formatCategory(cat)}
              </button>
            ))}
          </div>
        </div>

        {/* GRID */}
        {view === "grid" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            {loading ? (
              <div style={{ gridColumn: '1/-1', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px 0', color: '#6b7280' }}>
                <div style={{ width: 32, height: 32, border: '3px solid #e5e7eb', borderTopColor: '#1D9E75', borderRadius: '50%', animation: 'spin 1s linear infinite', marginRight: 12 }} />
                Loading places...
              </div>
            ) : places.length === 0 ? (
              <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '60px 0', color: '#6b7280' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>🏔️</div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1a2e1a', marginBottom: 6 }}>No places found</h3>
                <p style={{ fontSize: 14 }}>Try adjusting your search or category filter.</p>
              </div>
            ) : (
              places.map((place, i) => (
                <div
                  key={place.id || place._id || `${place.name}-${i}`}
                  style={{
                    background: 'white', borderRadius: 16, overflow: 'hidden',
                    border: '1px solid rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column'
                  }}
                >
                  {/* Image */}
                  <div style={{ position: 'relative', height: 200, overflow: 'hidden', flexShrink: 0 }}>
                    <ImageWithFallback
                      src={getPlaceImage(place, i)}
                      alt={place.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      fallbackSrc="/fallback.jpg"
                    />
                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.4) 0%, transparent 60%)' }} />

                    {/* Category badge */}
                    <div style={{
                      position: 'absolute', top: 12, left: 12,
                      background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)',
                      color: 'white', borderRadius: 999, padding: '4px 10px',
                      fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em'
                    }}>
                      {formatCategory(place.category || "other")}
                    </div>

                    {/* Rating */}
                    {place.avg_rating > 0 && (
                      <div style={{
                        position: 'absolute', top: 12, right: 12,
                        background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
                        color: 'white', borderRadius: 999, padding: '4px 10px',
                        fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4
                      }}>
                        <Star size={11} style={{ fill: '#fbbf24', color: '#fbbf24' }} />
                        {place.avg_rating}
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div style={{ padding: '16px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <div>
                      <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1a2e1a', marginBottom: 6, lineHeight: 1.3 }}>
                        {place.name}
                      </h3>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4, color: '#6b7280', fontSize: 12, marginBottom: 12 }}>
                        <MapPin size={13} style={{ color: '#1D9E75', flexShrink: 0, marginTop: 1 }} />
                        <span style={{ lineHeight: 1.4 }}>
                          {place.address?.trim()
                            ? place.address
                            : place.location?.name?.trim?.() || place.location_name || "Uttarakhand"}
                        </span>
                      </div>
                    </div>

                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      paddingTop: 12, borderTop: '1px solid rgba(0,0,0,0.06)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#6b7280' }}>
                          <Eye size={13} /> {place.views || 0}
                        </span>
                        <button
                          onClick={(e) => handleLike(e, place.id || place._id)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, 
                            color: place.liked_by?.includes(user?.id || user?._id) ? '#ef4444' : '#6b7280',
                            background: 'none', border: 'none', cursor: 'pointer', padding: 0
                          }}
                        >
                          <Heart size={13} style={{ fill: place.liked_by?.includes(user?.id || user?._id) ? '#ef4444' : 'none' }} /> {place.likes || 0}
                        </button>
                      </div>
                      <Link
                        to={`/places/${place.id || place._id}`}
                        style={{
                          color: '#1D9E75', fontWeight: 600, fontSize: 13,
                          textDecoration: 'none'
                        }}
                      >
                        Explore →
                      </Link>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* MAP */}
        {view === "map" && (
          <div style={{ borderRadius: 20, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)' }}>
            <MapContainer center={[30, 79]} zoom={7} style={{ height: "600px", width: "100%" }}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {mapPlaces
                .filter((p) => p?.location?.lat != null && p?.location?.lng != null)
                .map((p, i) => (
                  <Marker
                    key={p.id || p._id || `${p.name}-${i}`}
                    position={[p.location.lat, p.location.lng]}
                    icon={markerIcon}
                  >
                    <Popup>
                      <div style={{ fontWeight: 700, color: '#1a2e1a', fontSize: 14, marginBottom: 2 }}>{p.name}</div>
                      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: '#1D9E75', marginBottom: 6 }}>
                        {formatCategory(p.category || "")}
                      </div>
                      <Link to={`/places/${p.id || p._id}`} style={{ fontSize: 13, fontWeight: 500, color: '#1D9E75', textDecoration: 'none' }}>
                        View details →
                      </Link>
                    </Popup>
                  </Marker>
                ))}
            </MapContainer>
          </div>
        )}
      </div>
    </div>
  );
}
